import React, { useState, useEffect, useRef, useCallback } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import {colors} from '../components/Theme/WebTheme';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import PolylineIcon from '@mui/icons-material/Polyline';
import { GoogleMap, DrawingManager, Polygon } from '@react-google-maps/api';
import TableStyle from '../components/Table/Style';
import { useLocation } from 'react-router-dom';
import { SECONDORY_COLOR } from "../common/sharedFunctions";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 800,
    height: 600,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    borderRadius:15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapSearchContainer: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 10,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    width: 'calc(100% - 24px)',
    maxWidth: 520,
  },
  mapSearchRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  mapSearchInput: {
    flex: 1,
  },
  mapHint: {
    marginTop: 6,
    color: '#B3261E',
    fontSize: 12,
  }
}));


export default function Zones() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editZone
  } = api;

  const columns = [
    { title: t('zone_name'), field: 'zone_name',
        headerStyle: {textAlign: 'center'},
        cellStyle: {textAlign: 'center', borderRight: `1px solid ${colors.TABLE_BORDER}`},
    },
    // { title: t('city_name'), field: 'city_name',
    //     headerStyle: {textAlign: 'center'},
    //     cellStyle: {textAlign: 'center'},
    // },
     //{ title: t('zone_surge'), field: 'zone_surge', type: 'numeric', initialEditValue: 0},
    { title: t('active'),  field: 'active', type:'boolean', initialEditValue: true,
      headerStyle: {textAlign: 'center', borderLeft: `1px solid ${colors.TABLE_BORDER}`, borderRight: `1px solid ${colors.TABLE_BORDER}`},
      cellStyle:{textAlign:isRTL=== 'rtl' ?'right':'center', borderLeft: `1px solid ${colors.TABLE_BORDER}`, borderRight: `1px solid ${colors.TABLE_BORDER}`},
    },
    { title: t('zone_path'), field: 'zone_path', editable: 'never',
      headerStyle: {borderLeft: `1px solid ${colors.TABLE_BORDER}`, borderRight: `1px solid ${colors.TABLE_BORDER}`},
      cellStyle: {borderLeft: `1px solid ${colors.TABLE_BORDER}`, borderRight: `1px solid ${colors.TABLE_BORDER}`},
      render: rowData => rowData.zone_path? <span>{rowData.zone_path.slice([0], [2]).join(",")}...</span>:null
    }
  ];
  const settings = useSelector(state => state.settingsdata.settings);
  const [data, setData] = useState([]);
  const zonelistdata = useSelector(state => state.zonelistdata);
  const dispatch = useDispatch();
  const [open,setOpen] = useState(false);
  const [rowIndex,setRowIndex] = useState();
  const classes = useStyles();
  const rootRef = useRef(null);
  const [polygon, setPolygon] = useState();
  const [highlightedPath, setHighlightedPath] = useState([]);
  const [searchTown, setSearchTown] = useState('');
  const [searchError, setSearchError] = useState('');
  const [mylocation, setMylocation] = useState(null);
  const {state} = useLocation()
  const mapRef = useRef(null);

  useEffect(() => {
    if (zonelistdata.zones) {
      setData(zonelistdata.zones);
    }else{
      setData([]);
    }
  }, [zonelistdata.zones]);

  const handleClose = () => {
    setOpen(false);
    setSearchError('');
  }

  const fitPathBounds = useCallback((path) => {
    if (mapRef.current && path && path.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      for (let i = 0; i < path.length; i++) {
        bounds.extend(path[i]);
      }
      mapRef.current.fitBounds(bounds);
    }
  }, []);

  const convertPathToZoneCoords = (path) => path.map((point) => [point.lat, point.lng]);

  const normalizePoint = (point) => {
    if (!point) {
      return null;
    }
    if (Array.isArray(point) && point.length >= 2) {
      return { lat: Number(point[0]), lng: Number(point[1]) };
    }
    if (typeof point.lat === 'number' && typeof point.lng === 'number') {
      return { lat: point.lat, lng: point.lng };
    }
    return null;
  };

  const normalizePath = (path) => {
    if (!Array.isArray(path)) {
      return [];
    }
    return path
      .map((point) => normalizePoint(point))
      .filter((point) => point && Number.isFinite(point.lat) && Number.isFinite(point.lng));
  };

  const isPointOnSegment = (point, a, b) => {
    const epsilon = 1e-10;
    const cross = (point.lng - a.lng) * (b.lat - a.lat) - (point.lat - a.lat) * (b.lng - a.lng);
    if (Math.abs(cross) > epsilon) {
      return false;
    }
    const dot = (point.lat - a.lat) * (b.lat - a.lat) + (point.lng - a.lng) * (b.lng - a.lng);
    if (dot < -epsilon) {
      return false;
    }
    const lenSq = (b.lat - a.lat) * (b.lat - a.lat) + (b.lng - a.lng) * (b.lng - a.lng);
    return dot <= lenSq + epsilon;
  };

  const orientation = (a, b, c) => {
    const value = (b.lng - a.lng) * (c.lat - b.lat) - (b.lat - a.lat) * (c.lng - b.lng);
    if (Math.abs(value) < 1e-10) {
      return 0;
    }
    return value > 0 ? 1 : 2;
  };

  const doSegmentsIntersect = (p1, q1, p2, q2) => {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);

    if (o1 !== o2 && o3 !== o4) {
      return true;
    }

    if (o1 === 0 && isPointOnSegment(p2, p1, q1)) return true;
    if (o2 === 0 && isPointOnSegment(q2, p1, q1)) return true;
    if (o3 === 0 && isPointOnSegment(p1, p2, q2)) return true;
    if (o4 === 0 && isPointOnSegment(q1, p2, q2)) return true;

    return false;
  };

  const isPointInPath = (point, polygonPath) => {
    if (!point || !Array.isArray(polygonPath) || polygonPath.length < 3) {
      return false;
    }

    for (let i = 0; i < polygonPath.length; i++) {
      const next = (i + 1) % polygonPath.length;
      if (isPointOnSegment(point, polygonPath[i], polygonPath[next])) {
        return true;
      }
    }

    let inside = false;
    for (let i = 0, j = polygonPath.length - 1; i < polygonPath.length; j = i++) {
      const xi = polygonPath[i].lat;
      const yi = polygonPath[i].lng;
      const xj = polygonPath[j].lat;
      const yj = polygonPath[j].lng;
      const intersects = ((yi > point.lng) !== (yj > point.lng))
        && (point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi);
      if (intersects) {
        inside = !inside;
      }
    }
    return inside;
  };

  const doPolygonsCollide = (pathA, pathB) => {
    const polygonA = normalizePath(pathA);
    const polygonB = normalizePath(pathB);

    if (polygonA.length < 3 || polygonB.length < 3) {
      return false;
    }

    for (let i = 0; i < polygonA.length; i++) {
      const a1 = polygonA[i];
      const a2 = polygonA[(i + 1) % polygonA.length];

      for (let j = 0; j < polygonB.length; j++) {
        const b1 = polygonB[j];
        const b2 = polygonB[(j + 1) % polygonB.length];
        if (doSegmentsIntersect(a1, a2, b1, b2)) {
          return true;
        }
      }
    }

    return isPointInPath(polygonA[0], polygonB) || isPointInPath(polygonB[0], polygonA);
  };

  const getCollidingZoneNames = (candidatePath) => {
    if (!Array.isArray(candidatePath) || candidatePath.length < 3 || rowIndex === undefined) {
      return [];
    }

    const currentZone = data[rowIndex];

    return data
      .filter((zone, index) => {
        if (index === rowIndex || !zone || !Array.isArray(zone.zone_path) || zone.zone_path.length < 3) {
          return false;
        }
        if (currentZone?.id && zone.id && currentZone.id === zone.id) {
          return false;
        }
        return doPolygonsCollide(candidatePath, zone.zone_path);
      })
      .map((zone) => zone.zone_name || zone.id || t('zone_name'));
  };

  const getCollisionErrorMessage = (collidingZoneNames) => {
    return `Zone path collides with: ${collidingZoneNames.join(', ')}`;
  };

  const handlePolygonComplete = (drawnPolygon) => {
    const arr = [];
    const zone = { ...data[rowIndex] };
    drawnPolygon.getPaths().getArray().forEach(path => {
      path.getArray().forEach((position) => arr.push([ position.lat(), position.lng()]));
    });

    const collidingZoneNames = getCollidingZoneNames(arr);
    if (collidingZoneNames.length > 0) {
      setSearchError(getCollisionErrorMessage(collidingZoneNames));
      drawnPolygon.setMap(null);
      return;
    }

    setSearchError('');
    zone['zone_path'] = arr;
    dispatch(editZone(zone, "Edit"));
    setOpen(false);
  };

  const getPathFromBounds = (bounds) => {
    if (!bounds) {
      return [];
    }
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    return [
      { lat: northEast.lat(), lng: southWest.lng() },
      { lat: northEast.lat(), lng: northEast.lng() },
      { lat: southWest.lat(), lng: northEast.lng() },
      { lat: southWest.lat(), lng: southWest.lng() },
    ];
  };

  const handleTownSearch = () => {
    if (!searchTown || !searchTown.trim()) {
      setSearchError(t('no_details_error'));
      return;
    }
    if (!window.google || !window.google.maps || !mapRef.current) {
      setSearchError('Google Maps is not ready yet.');
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchTown.trim() }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const firstResult = results[0];
        const resultLocation = firstResult.geometry?.location;
        const bounds = firstResult.geometry?.bounds || firstResult.geometry?.viewport;
        setSearchError('');

        if (resultLocation) {
          setMylocation({
            lat: resultLocation.lat(),
            lng: resultLocation.lng()
          });
        }

        if (bounds) {
          const boundaryPath = getPathFromBounds(bounds);
          setHighlightedPath(boundaryPath);
          fitPathBounds(boundaryPath);
        } else if (resultLocation) {
          mapRef.current.panTo(resultLocation);
          mapRef.current.setZoom(13);
          setHighlightedPath([]);
        }
      } else {
        setHighlightedPath([]);
        setSearchError('No area found. Try a more specific town/city name.');
      }
    });
  };

  const useHighlightedBoundary = () => {
    if (!highlightedPath || highlightedPath.length === 0 || rowIndex === undefined) {
      return;
    }

    const zonePath = convertPathToZoneCoords(highlightedPath);
    const collidingZoneNames = getCollidingZoneNames(zonePath);
    if (collidingZoneNames.length > 0) {
      setSearchError(getCollisionErrorMessage(collidingZoneNames));
      return;
    }

    setSearchError('');
    const zone = { ...data[rowIndex] };
    zone.zone_path = zonePath;
    dispatch(editZone(zone, 'Edit'));
    setPolygon(highlightedPath);
    setOpen(false);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const fitPolygonBounds = useCallback(() => {
    fitPathBounds(polygon);
  }, [fitPathBounds, polygon]);

  useEffect(() => {
    if (open) {
      fitPolygonBounds();
    }
  }, [open, fitPolygonBounds]);

  return (
    zonelistdata.loading ? <CircularLoading /> :
    <div ref={rootRef}>
      <MaterialTable
        title={t('zones')}
        columns={columns}
        data={data}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}

        style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: "20px",
        }}
        options={{
            initialPage:state?.pageNo,
            rowStyle: (rowData) => ({
                backgroundColor:
                selectedRow === rowData.tableData.id ? colors.ROW_SELECTED :colors.WHITE
            }),
            ...TableStyle()
        }}
        localization={{body:{
          addTooltip: (t('add')),
          deleteTooltip: (t('delete')),
          editTooltip: (t('edit')),
          emptyDataSourceMessage: (
            (t('blank_message'))
        ),
        editRow: { 
          deleteText: (t('delete_message')),
          cancelTooltip: (t('cancel')),
          saveTooltip: (t('save')) 
            }, 
          },
          toolbar: {
            searchPlaceholder: (t('search')),
            exportTitle: (t('export')),
          },
          pagination: {
            labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
        }}
        editable={settings.AllowCriticalEditsAdmin ? {
            onRowAdd: newData =>
            new Promise((resolve, reject)=> {
              setTimeout(() => {
                if(!(newData && newData.zone_name)){
                  alert(t('no_details_error'));
                  reject();
                }else{
                  dispatch(editZone(newData, "Add"));
                  resolve();
                }
              }, 600);
            }),
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject)=> {
              setTimeout(() => {
                if(!(newData && newData.zone_name)){
                  alert(t('no_details_error'));
                  reject();
                }else {
                  dispatch(editZone(newData, "Update"));
                  resolve()
                }
              }, 600);
            }),
          onRowDelete: oldData =>
            new Promise(resolve => {
              setTimeout(() => {
                resolve();
                dispatch(editZone(oldData, "Delete"));
              }, 600);
            }),
        } : null}
        actions={[
          rowData => ({
            icon: () => <div style={{display: 'flex',alignItems: 'center',flexWrap: 'wrap'}}>
                <PolylineIcon />
                <Typography variant="subtitle2" style={{padding: 5}}>{t('edit_zone_path')}</Typography>
            </div>,
            onClick: (event, rowData) => {
              setRowIndex(rowData.tableData.id);
              setOpen(true);
              setSearchTown(rowData.zone_name || '');
              setSearchError('');
              setHighlightedPath([]);
              if(rowData.zone_path && rowData.zone_path.length>0){
                let arr = [];
                for(let i=0;i<rowData.zone_path.length;i++){
                  const path = rowData.zone_path[i];
                  arr.push(new window.google.maps.LatLng(path[0], path[1]));
                }
                setPolygon(arr);
                let obj = JSON.stringify(arr[0]);
                let obj2 = JSON.parse(obj);
                setMylocation({
                  lat: obj2.lat,
                  lng: obj2.lng
                })
              } else{
                setMylocation(null);
                setPolygon();
              }
            }
          })
        ]}
      />
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        onClose={handleClose}
        open={open}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <div className={classes.paper} style={{ position: 'relative' }}>
          <div className={classes.mapSearchContainer}>
            <div className={classes.mapSearchRow}>
              <TextField
                size="small"
                className={classes.mapSearchInput}
                value={searchTown}
                onChange={(e) => setSearchTown(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTownSearch();
                  }
                }}
                placeholder={`${t('search')} town/city`}
                variant="outlined"
              />
              <Button variant="contained" color="primary" onClick={handleTownSearch}>
                {t('search')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={useHighlightedBoundary}
                disabled={!highlightedPath || highlightedPath.length === 0}
              >
                Use Highlighted Boundary
              </Button>
            </div>
            {searchError ? <Typography className={classes.mapHint}>{searchError}</Typography> : null}
          </div>
          <GoogleMap
              zoom={polygon? 8 : 5}
              center={{ lat: mylocation? mylocation.lat : 20.5937, lng: mylocation? mylocation.lng : 78.9629 }}
              mapContainerStyle={{ height: '100%' }}
              onLoad={(map)=>{ mapRef.current = map; fitPolygonBounds(); }}
          >
              <DrawingManager
                  drawingMode={'polygon'}
                  options={{drawingControl: false}}
                  onPolygonComplete={handlePolygonComplete}
              />
              {polygon && polygon.length > 0 && (
                <Polygon paths={polygon} />
              )}
              {highlightedPath && highlightedPath.length > 0 && (
                <Polygon
                  paths={highlightedPath}
                  options={{
                    strokeColor: '#0E9F6E',
                    strokeWeight: 2,
                    fillColor: '#0E9F6E',
                    fillOpacity: 0.18,
                  }}
                />
              )}
          </GoogleMap>
        </div>
      </Modal>
    </div>
  );
}
